import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { useGetMoneyTransferRequestsByOrderQuery } from "store/queries/moneyTransfers";
import {
  IMoneyTransferRequest,
  MoneyTransferRequestStatus,
} from "types/MoneyTransfers";
// Helper function to format time difference
const formatTimeDistance = (date: Date): string => {
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffMins = Math.ceil(diffMs / (1000 * 60));

  if (diffMins < 60) {
    return `${diffMins} minute${diffMins !== 1 ? "s" : ""}`;
  }

  const diffHours = Math.ceil(diffMins / 60);
  return `${diffHours} hour${diffHours !== 1 ? "s" : ""}`;
};

interface IMoneyTransferRequestStatusProps {
  orderId: string;
  /** Optional pre-fetched data - if provided, skips internal query */
  data?: IMoneyTransferRequest[];
  /** Optional refetch function when using pre-fetched data */
  onRefetch?: () => void;
  /** Whether to show in compact mode (no empty state, smaller scroll height) */
  compact?: boolean;
}

export function MoneyTransferRequestsList({
  orderId,
  data: externalData,
  onRefetch,
  compact = false,
}: IMoneyTransferRequestStatusProps) {
  // Only fetch if external data is not provided
  const {
    data: requestsData,
    isLoading,
    error,
    refetch: internalRefetch,
  } = useGetMoneyTransferRequestsByOrderQuery(orderId, {
    skip: externalData !== undefined,
  });

  const refetch = onRefetch || internalRefetch;

  const getStatusSeverity = (status: MoneyTransferRequestStatus) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "PROCESSING":
        return "info";
      case "COMPLETED":
        return "success";
      case "FAILED":
        return "danger";
      case "CANCELLED":
        return "info";
      default:
        return "info";
    }
  };

  const statusBodyTemplate = (rowData: IMoneyTransferRequest) => {
    return (
      <Tag
        value={rowData.status}
        severity={getStatusSeverity(rowData.status)}
      />
    );
  };

  const scheduledAtBodyTemplate = (rowData: IMoneyTransferRequest) => {
    const scheduledDate = new Date(rowData.scheduledAt);
    const now = new Date();

    if (scheduledDate > now) {
      return (
        <div>
          <div>{scheduledDate.toLocaleString()}</div>
          <small className="text-muted">
            (in {formatTimeDistance(scheduledDate)})
          </small>
        </div>
      );
    }

    return scheduledDate.toLocaleString();
  };

  const processedAtBodyTemplate = (rowData: IMoneyTransferRequest) => {
    if (!rowData.processedAt) return "-";
    return new Date(rowData.processedAt).toLocaleString();
  };

  const toFromBodyTemplate = (rowData: IMoneyTransferRequest) => {
    const item = rowData.transferItems[0];
    if (!item) return "-";

    let fromName = "";
    let toName = "";

    // Get From name
    if (item.fromPartyType === "PROVIDER") {
      if (item.fromProvider) {
        fromName = item.fromProvider.name;
      } else if (item.fromProviderId) {
        // If provider object is not populated, show the party type
        fromName = "Provider";
      }
    } else if (item.fromPartyType === "CLEAR_HEALTH") {
      fromName = "Clear Health";
    } else {
      fromName = item.fromPartyType || "Unknown";
    }

    // Get To name
    if (item.toPartyType === "PROVIDER") {
      if (item.toProvider) {
        toName = item.toProvider.name;
      } else if (item.toProviderId) {
        // If provider object is not populated, show the party type
        toName = "Provider";
      }
    } else if (item.toPartyType === "PATIENT") {
      if (item.toPatient) {
        toName = `${item.toPatient.firstName} ${item.toPatient.lastName}`;
      } else if (item.toPatientId) {
        // If patient object is not populated, show the party type
        toName = "Patient";
      }
    } else if (item.toPartyType === "CLEAR_HEALTH") {
      toName = "Clear Health";
    } else {
      toName = item.toPartyType || "Unknown";
    }

    return (
      <div style={{ whiteSpace: "normal" }}>
        <div style={{ marginBottom: "2px" }}>From: {fromName}</div>
        <div>
          To:{" "}
          {item.toPartyType === "PATIENT" ? (
            <span data-pii="patient-name">{toName}</span>
          ) : (
            toName
          )}
        </div>
      </div>
    );
  };

  const amountBodyTemplate = (rowData: IMoneyTransferRequest) => {
    const item = rowData.transferItems[0];
    if (!item) return "-";

    return `$${(item.amountInCents / 100).toFixed(2)}`;
  };

  const noteBodyTemplate = (rowData: IMoneyTransferRequest) => {
    if (!rowData.note) return "-";
    return (
      <div title={rowData.note}>
        {rowData.note.length > 50
          ? `${rowData.note.substring(0, 50)}...`
          : rowData.note}
      </div>
    );
  };

  // Use external data if provided, otherwise use query data
  const requests = externalData ?? requestsData?.data ?? [];

  // Only show loading/error states when not using external data
  if (!externalData) {
    if (isLoading) {
      return (
        <div className="text-center p-3">
          Loading money transfer requests...
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center p-3">
          <p className="text-red-600">Error loading money transfer requests</p>
          <Button
            label="Retry"
            onClick={() => refetch()}
            className="p-button-sm"
          />
        </div>
      );
    }
  }

  if (requests.length === 0) {
    // In compact mode, render nothing if no data
    if (compact) {
      return null;
    }
    return (
      <div className="text-center p-3 text-muted">
        No money transfer requests found for this order.
      </div>
    );
  }

  return (
    <div>
      {!compact && (
        <div className="flex justify-content-between align-items-center mb-3">
          <div />
          <Button
            icon="pi pi-refresh"
            onClick={() => refetch()}
            className="p-button-text p-button-sm"
            tooltip="Refresh"
          />
        </div>
      )}

      <DataTable
        value={requests}
        size="small"
        stripedRows
        scrollable
        scrollHeight={compact ? "200px" : "400px"}
      >
        <Column
          field="status"
          header="Status"
          body={statusBodyTemplate}
          style={{ minWidth: "100px", maxWidth: "100px" }}
        />
        <Column
          field="scheduledAt"
          header="Scheduled"
          body={scheduledAtBodyTemplate}
          style={{ minWidth: "120px", maxWidth: "120px" }}
        />
        <Column
          field="processedAt"
          header="Processed"
          body={processedAtBodyTemplate}
          style={{ minWidth: "120px", maxWidth: "120px" }}
        />
        <Column
          header="From/To"
          body={toFromBodyTemplate}
          style={{ minWidth: "280px" }}
        />
        <Column
          header="Amount"
          body={amountBodyTemplate}
          style={{ minWidth: "80px", maxWidth: "100px" }}
        />
        <Column
          field="note"
          header="Note"
          body={noteBodyTemplate}
          style={{ minWidth: "100px", maxWidth: "150px", textAlign: "left" }}
          headerStyle={{ textAlign: "left" }}
        />
        <Column
          field="user.name"
          header="Created By"
          style={{ minWidth: "120px", maxWidth: "150px" }}
        />
      </DataTable>
    </div>
  );
}
